import { useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useAuthGateStore, type AuthGateView } from '../store/authGateStore';
import { useUIStore } from '../store/uiStore';
import { anonWritesBlocked } from '../constants/auth';

interface RequireAuthOptions {
  message?: string;
  /**
   * Route to land on after auth completes when the user still has to go
   * through onboarding. Without this, the pending callback's `router.push`
   * gets clobbered by the onboarding redirect and the user ends up on the
   * default tab instead of the page they were trying to reach.
   */
  returnPath?: string;
  /**
   * Let an anonymous Fire Scout through even while ANON_WRITES_ENABLED is
   * false. Set this on read/navigation call sites (open a post, switch
   * tabs) so existing anonymous accounts can still browse; leave it off
   * for writes, which route to the upgrade sheet instead. Mirrors the
   * server-side requireAuth({ allowAnonymous }) default in
   * functions/src/utils/validation.ts.
   */
  allowAnonymous?: boolean;
  /** Which form the gate opens on. See OpenOptions in authGateStore. */
  initialView?: AuthGateView;
}

export function useAuthGate() {
  const user = useAuthStore((s) => s.user);
  const open = useAuthGateStore((s) => s.open);

  const requireAuth = useCallback(
    (callback: () => void, options?: RequireAuthOptions) => {
      if (!options?.allowAnonymous && anonWritesBlocked(user)) {
        // Anonymous Fire Scout while writes are flag-disabled: the action
        // needs a durable account, so route to the save-account sheet
        // instead of running the callback. See ANON_WRITES_ENABLED.
        useUIStore.getState().openSheet('upgradeAccount');
      } else if (user) {
        callback();
      } else {
        open(callback, {
          message: options?.message,
          initialView: options?.initialView,
          returnPath: options?.returnPath,
        });
      }
    },
    [user, open]
  );

  return { requireAuth };
}
